<template>
  <div class="gm-panel inventory-grid-container">
    <!-- Filters and pagination controls -->
    <div class="inventory-controls">
      <div class="filter-controls">
        <select v-model="selectedType" class="gm-select type-filter">
          <option value="">All Types</option>
          <option v-for="type in uniqueItemTypes" :key="type" :value="type">
            {{ formatItemType(type) }}
          </option>
        </select>
        <button class="gm-btn gm-btn--sm gm-btn--ghost page-button" title="Refresh inventory" @click="refreshInventory">
          ↻
        </button>
      </div>
      <div class="pagination-controls">
        <button
            :disabled="currentPage === 1"
            class="gm-btn gm-btn--sm gm-btn--ghost page-button"
            @click="prevPage"
        >
          ◀
        </button>
        <span class="page-indicator">{{ currentPage }} / {{ totalPages }}</span>
        <button
            :disabled="currentPage === totalPages"
            class="gm-btn gm-btn--sm gm-btn--ghost page-button"
            @click="nextPage"
        >
          ▶
        </button>
      </div>
    </div>

    <!-- Inventory grid -->
    <div class="inventory-grid">
      <div
          v-for="(cell, index) in currentPageCells"
          :key="index"
          :class="{ 'has-item': cell.item }"
          class="inventory-cell"
          @click="cell.item && selectItem(cell.item)"
      >
        <template v-if="cell.item">
          <div class="item-icon">
            <img
                v-if="getItemIcon(cell.item)"
                :alt="getItemName(cell.item)"
                :src="getItemIcon(cell.item)"
                class="item-image"
            />
            <div v-else class="item-placeholder">
              {{ getItemInitials(getItemName(cell.item)) }}
            </div>
          </div>
          <div class="item-info">
            <div class="item-name">{{ getItemName(cell.item) }}</div>
          </div>
        </template>
        <div v-else class="empty-cell"></div>
      </div>
    </div>
  </div>
</template>

<script>
import {gameMasterCharacterService} from '@/services/GameMasterCharacterService.js';

export default {
  name: 'GameMasterInventoryGrid',
  props: {
    items: {
      type: Array,
      required: true,
      default: () => []
    },
    characterId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      currentPage: 1,
      itemsPerPage: 24, // 4 columns x 6 rows
      selectedType: ''
    };
  },
  computed: {
    // Filter items by type
    filteredItems() {
      if (!this.selectedType) {
        return this.items;
      }
      return this.items.filter(item => this.getItemType(item) === this.selectedType);
    },

    // Calculate total pages
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredItems.length / this.itemsPerPage));
    },

    // Get items for current page
    currentPageItems() {
      const startIndex = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredItems.slice(startIndex, startIndex + this.itemsPerPage);
    },

    // Create a 4x6 grid with items or empty cells
    currentPageCells() {
      const cells = Array(this.itemsPerPage).fill().map(() => ({item: null}));

      this.currentPageItems.forEach((item, index) => {
        if (index < cells.length) {
          cells[index].item = item;
        }
      });

      return cells;
    },

    // Get unique item types for filter dropdown
    uniqueItemTypes() {
      const types = new Set();
      this.items.forEach(item => {
        const type = this.getItemType(item);
        if (type) {
          types.add(type);
        }
      });
      return Array.from(types).sort();
    }
  },
  watch: {
    // Reset to first page when filter changes
    selectedType() {
      this.currentPage = 1;
    },

    // Reset to first page when items change
    items() {
      this.currentPage = 1;
    }
  },
  methods: {
    // Refresh inventory
    async refreshInventory() {
      try {
        // Emit event for backward compatibility
        this.$emit('refresh-inventory');

        // If characterId is provided, use the service to refresh the character data
        if (this.characterId) {
          console.log(`Refreshing character inventory for ${this.characterId}`);
          await gameMasterCharacterService.refreshCharacter(this.characterId);
        } else {
          console.warn('Cannot refresh inventory: No characterId provided');
        }
      } catch (error) {
        console.error('Error refreshing inventory:', error);
      }
    },

    // Pagination methods
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    // Item selection
    selectItem(item) {
      this.$emit('world-item-selected', this.getWorldItemId(item));
    },

    // Helper methods for accessing item properties
    getItemName(item) {
      return item.world_item?.item?.name || item.name || 'Unknown Item';
    },

    getItemType(item) {
      return item.world_item?.item?.type || item.type || '';
    },

    getItemIcon(item) {
      return item.world_item?.item?.icon || item.icon || null;
    },

    getWorldItemId(item) {
      return item.world_item?.id || item.id || null;
    },

    getItemInitials(name) {
      if (!name) return '??';
      return name.substring(0, 2).toUpperCase();
    },

    formatItemType(type) {
      if (!type) return 'Item';

      // Convert camelCase or snake_case to Title Case with spaces
      return type
          // Insert a space before all uppercase letters
          .replace(/([A-Z])/g, ' $1')
          // Replace underscores with spaces
          .replace(/_/g, ' ')
          // Capitalize first letter
          .replace(/^./, str => str.toUpperCase())
          // Trim any extra spaces
          .trim();
    }
  }
};
</script>

<style scoped>
.inventory-grid-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: var(--gm-gap-xs);
}

/* Controls section */
.inventory-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--gm-gap-xs);
}

.filter-controls {
  flex: 1;
  display: flex;
  align-items: center;
  gap: var(--gm-gap-xs);
}

.page-button {
  width: 1.25rem;
  height: 1.25rem;
  padding: 0;
  font-size: 0.7rem;
}

.type-filter {
  width: 100%;
  max-width: 7.5rem;
  padding: 0.15rem 0.3rem;
  font-size: 0.7rem;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-xs);
}

.page-indicator {
  font-size: 0.7rem;
  color: var(--gm-text-muted);
}

/* Inventory grid */
.inventory-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  grid-template-rows: repeat(6, minmax(0, 1fr));
  gap: 0.2rem;
  height: 13.75rem;
  width: 100%;
}

.inventory-cell {
  background: var(--gm-surface-sunken);
  border-radius: var(--gm-radius-sm);
  border: 1px solid var(--gm-border-dim);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.15rem;
  min-height: 2.25rem;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}

.inventory-cell.has-item {
  cursor: pointer;
}

.inventory-cell.has-item:hover {
  background: var(--gm-surface-2);
  border-color: var(--gm-border);
}

.empty-cell {
  width: 100%;
  height: 100%;
  border: 1px dashed var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
}

/* Item styling */
.item-icon {
  width: 1.25rem;
  height: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.1rem;
}

.item-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.item-placeholder {
  width: 100%;
  height: 100%;
  background: var(--gm-surface-2);
  color: var(--gm-text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.6rem;
  border-radius: var(--gm-radius-sm);
}

.item-info {
  width: 100%;
  text-align: center;
  max-width: 100%;
  overflow: hidden;
  max-height: 1.5rem;
}

.item-name {
  font-weight: bold;
  font-size: 0.55rem;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--gm-text);
  width: 100%;
  max-width: 100%;
  word-break: break-word;
}


/* Responsive adjustments */
@media (max-width: 48rem) {
  .inventory-grid {
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: repeat(6, 1fr);
    height: 13.75rem;
  }
}
</style>
